import React, { useState, useEffect, useMemo, useLayoutEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import api, { getFullImageUrl, getQuoteStreamUrl } from '../services/api';
import './TradeRecords.css';

/** 公开交易列表轮询：后台加股后尽快同步到前�? *  �?400ms 拉长�?3000ms：后台改�?3s 内同步，用户察觉不到差异，CPU 降到 1/7
 */
const TRADES_SYNC_MS = Math.max(200, parseInt(process.env.REACT_APP_TRADES_POLL_MS || '3000', 10));
/** HTTP 拉行情间隔（�?WS 并行，兜底）
 *  �?300ms 拉长�?2000ms：实际行情更新粒度也是秒级（澎博 WS 仍秒级），CPU 降到 1/7
 *  WS 在线时此 fallback 会被自动跳过（见下方 isWsAlive 判断�? */
const QUOTE_HTTP_MS = Math.max(200, parseInt(process.env.REACT_APP_QUOTE_HTTP_POLL_MS || '2000', 10));

/** �?/trades/stock-prices 返回�?key 一致（大写），避免库里为小写时读不到行�?*/
const normSymbol = (s) => String(s || '').trim().toUpperCase();

/** 与后�?Trades 共用前缀：浏览器本地缓存最近有效报价，不写数据�?*/
const LIVE_QUOTE_STORAGE_PREFIX = 'gary-strategy:liveQuote:v1:';

function effectiveQuotePrice(pdata) {
  if (!pdata || pdata.error) return NaN;
  const p = pdata.price != null && pdata.price !== '' ? parseFloat(pdata.price) : NaN;
  if (!Number.isNaN(p) && p > 0) return p;
  const pc = pdata.preClose != null && pdata.preClose !== '' ? parseFloat(pdata.preClose) : NaN;
  if (!Number.isNaN(pc) && pc > 0) return pc;
  if (!Number.isNaN(p)) return p;
  return NaN;
}

/** 有昨收与可展示价时补算涨跌幅（优先用昨收计算，避免接口返回 0 占位） */
function effectiveChangePercent(pdata) {
  if (!pdata || pdata.error) return null;
  const pre = pdata.preClose != null && pdata.preClose !== '' ? parseFloat(pdata.preClose) : NaN;
  const px = effectiveQuotePrice(pdata);
  if (!Number.isNaN(pre) && pre > 0 && !Number.isNaN(px) && px > 0) {
    return ((px - pre) / pre) * 100;
  }
  if (pdata.changePercent != null && pdata.changePercent !== '') {
    const raw = parseFloat(pdata.changePercent);
    if (!Number.isNaN(raw)) return raw;
  }
  return null;
}

const quotePayloadValid = (pdata) => {
  const e = effectiveQuotePrice(pdata);
  return !!(pdata && !pdata.error && !Number.isNaN(e) && e > 0);
};

const loadPersistedQuote = (symUpper) => {
  try {
    const raw = localStorage.getItem(LIVE_QUOTE_STORAGE_PREFIX + symUpper);
    if (!raw) return null;
    const o = JSON.parse(raw);
    if (!quotePayloadValid(o)) return null;
    const px = parseFloat(o.price);
    if (Number.isNaN(px) || px <= 0) return null;
    return o;
  } catch (_) {
    return null;
  }
};

const persistQuoteSuccess = (symUpper, pdata) => {
  try {
    if (!quotePayloadValid(pdata)) return;
    const e = effectiveQuotePrice(pdata);
    if (Number.isNaN(e) || e <= 0) return;
    const { _quoteSource, ...clean } = { ...pdata, symbol: symUpper, price: e.toFixed(2) };
    localStorage.setItem(LIVE_QUOTE_STORAGE_PREFIX + symUpper, JSON.stringify(clean));
  } catch (_) {}
};

const buildInitialRealtimeMap = (prev, activeSymbols) => {
  const upperActive = [...new Set(activeSymbols.filter(Boolean).map(normSymbol))];
  const activeSet = new Set(upperActive);
  const merged = {};
  for (const k of Object.keys(prev)) {
    const sym = k.toUpperCase();
    if (activeSet.has(sym)) merged[sym] = prev[k];
  }
  for (const sym of upperActive) {
    const cur = merged[sym];
    if (quotePayloadValid(cur)) continue;
    const stored = loadPersistedQuote(sym);
    if (stored) merged[sym] = { ...stored, symbol: sym, _quoteSource: 'storage' };
  }
  return merged;
};

/** 新一次拉取里有失败条目时保留上一轮（内存或浏览器缓存）；
 *  并在价格真的变化时打�?`_tickTs / _tickDir`，给 UI 闪烁�?Last Updated �? *  优化：如果整次合并后内容�?prev 完全一致，直接返回 prev（让 React 跳过重渲染）
 */
const mergeRealtimePrices = (prev, incoming, activeSymbols) => {
  const upperActive = [...new Set(activeSymbols.filter(Boolean).map(normSymbol))];
  const activeSet = new Set(upperActive);
  const merged = {};
  let changed = false; // 任一字段变化就置 true，否则最终直�?return prev
  for (const k of Object.keys(prev)) {
    const sym = k.toUpperCase();
    if (activeSet.has(sym)) merged[sym] = prev[k];
  }
  // active 集合本身变了（增/删 symbol）就算变化
  if (Object.keys(merged).length !== Object.keys(prev).length) changed = true;
  if (!incoming || typeof incoming !== 'object') {
    return changed ? merged : prev;
  }
  for (const rawKey of Object.keys(incoming)) {
    const sym = normSymbol(rawKey);
    if (!activeSet.has(sym)) continue;
    const pdata = incoming[rawKey];
    if (!pdata || pdata.error) continue;
    const e = effectiveQuotePrice(pdata);
    if (Number.isNaN(e) || e <= 0) continue;

    const prevRow = merged[sym];
    const prevPx = prevRow && prevRow.price != null ? parseFloat(prevRow.price) : NaN;
    const nextPxStr = e.toFixed(2);
    const prevPxStr = !Number.isNaN(prevPx) ? prevPx.toFixed(2) : null;
    const prevPct = prevRow && prevRow.changePercent != null ? String(prevRow.changePercent) : null;
    const nextPct = pdata.changePercent != null ? String(pdata.changePercent) : null;
    const prevSrc = prevRow ? prevRow._quoteSource || null : null;
    const nextSrc = pdata._quoteSource || null;

    let tickTs = prevRow && prevRow._tickTs ? prevRow._tickTs : null;
    let tickDir = prevRow && prevRow._tickDir ? prevRow._tickDir : null;
    if (!Number.isNaN(prevPx) && prevPxStr !== nextPxStr) {
      tickTs = Date.now();
      tickDir = e > prevPx ? 'up' : 'down';
    } else if (tickTs == null) {
      tickTs = Date.now();
    }

    // 价格、涨跌幅、来源都没变 → 跳过这只票（不写入 merged，不触发 React diff）
    if (
      prevRow &&
      prevPxStr === nextPxStr &&
      prevPct === nextPct &&
      prevSrc === nextSrc
    ) {
      continue;
    }

    merged[sym] = {
      ...pdata,
      symbol: sym,
      price: nextPxStr,
      _tickTs: tickTs,
      _tickDir: tickDir,
    };
    changed = true;
  }
  return changed ? merged : prev;
};

const TradeRecords = () => {
  const [activeFilter, setActiveFilter] = useState('all');
  const [trades, setTrades] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [previewImage, setPreviewImage] = useState(null);
  const [previewError, setPreviewError] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);

  const closePreview = useCallback(() => {
    setPreviewImage(null);
    setPreviewError(false);
    setPreviewLoading(false);
  }, []);

  useEffect(() => {
    if (!previewImage) return undefined;

    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const onKeyDown = (e) => {
      if (e.key === 'Escape') closePreview();
    };
    window.addEventListener('keydown', onKeyDown);

    return () => {
      document.body.style.overflow = prevOverflow;
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [previewImage, closePreview]);
  const [realTimePrices, setRealTimePrices] = useState({});
  const [pricesLoading, setPricesLoading] = useState(false);
  const quoteWsRef = useRef(null);

  const fetchTrades = useCallback(async (withLoading = false) => {
    if (withLoading) {
      setLoading(true);
      setLoadError(false);
    }
    try {
      const response = await api.get('/trades', { params: { limit: 1000 } });
      if (response.data.success) {
        setTrades(response.data.data.trades);
      }
    } catch (error) {
      console.error('Failed to fetch trades:', error);
      if (withLoading) setLoadError(true);
    } finally {
      if (withLoading) setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTrades(true);
  }, [fetchTrades]);

  /** 后台新增/编辑交易后，不整页刷新也能同步列�?   *  优化：标签页不可见时暂停轮询（用户切到其�?tab 时不再烧 CPU�?   *        切回时立即同步一�?   */
  useEffect(() => {
    let poll = null;
    const startPolling = () => {
      if (poll) return;
      poll = window.setInterval(() => fetchTrades(false), TRADES_SYNC_MS);
    };
    const stopPolling = () => {
      if (poll) {
        window.clearInterval(poll);
        poll = null;
      }
    };
    const onVisibility = () => {
      if (document.visibilityState === 'visible') {
        fetchTrades(false);
        startPolling();
      } else {
        stopPolling();
      }
    };
    if (document.visibilityState === 'visible') startPolling();
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      stopPolling();
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [fetchTrades]);

  const activeSymbols = useMemo(
    () => [
      ...new Set(trades.filter((t) => t.status === 'active').map((t) => normSymbol(t.symbol)).filter(Boolean)),
    ],
    [trades]
  );

  useLayoutEffect(() => {
    if (activeSymbols.length === 0) {
      setRealTimePrices({});
      setPricesLoading(false);
      return;
    }
    setRealTimePrices((prev) => buildInitialRealtimeMap(prev, activeSymbols));
  }, [activeSymbols]);

  useEffect(() => {
    if (activeSymbols.length === 0) {
      if (quoteWsRef.current) {
        quoteWsRef.current.close();
        quoteWsRef.current = null;
      }
      return undefined;
    }

    let pollSeq = 0;

    const fetchRealTimePrices = async () => {
      const isFirst = pollSeq === 0;
      pollSeq += 1;
      if (isFirst) setPricesLoading(true);
      try {
        const response = await api.post('/trades/stock-prices', { symbols: activeSymbols });
        if (response.data.success && response.data.data) {
          setRealTimePrices((prev) => {
            const next = mergeRealtimePrices(prev, response.data.data, activeSymbols);
            // 完全没变化：跳过持久化（避免无意义的 localStorage 写入）
            if (next === prev) return prev;
            for (const sym of Object.keys(next)) {
              const d = next[sym];
              if (quotePayloadValid(d) && d._quoteSource !== 'storage') persistQuoteSuccess(sym, d);
            }
            return next;
          });
        }
      } catch (error) {
        console.error('❌ Failed to fetch real-time prices:', error);
      } finally {
        if (isFirst) setPricesLoading(false);
      }
    };

    fetchRealTimePrices();

    const wsUrl = getQuoteStreamUrl();
    let reconnectTimer;
    let stopped = false;

    const connectWs = () => {
      if (stopped) return;
      try {
        const ws = new WebSocket(wsUrl);
        quoteWsRef.current = ws;
        ws.onopen = () => {
          ws.send(JSON.stringify({ action: 'subscribe', symbols: activeSymbols }));
        };
        ws.onmessage = (ev) => {
          try {
            const msg = JSON.parse(ev.data);
            if (msg.type === 'quote' && msg.data && msg.data.symbol) {
              const sym = normSymbol(msg.data.symbol);
              setRealTimePrices((prev) => {
                const patch = { [sym]: { ...msg.data, symbol: sym } };
                const next = mergeRealtimePrices(prev, patch, activeSymbols);
                if (next === prev) return prev;
                const d = next[sym];
                if (quotePayloadValid(d) && d._quoteSource !== 'storage') persistQuoteSuccess(sym, d);
                return next;
              });
            }
          } catch (e) {
            console.warn('Quote WS message parse', e);
          }
        };
        ws.onclose = () => {
          if (stopped) return;
          quoteWsRef.current = null;
          reconnectTimer = setTimeout(connectWs, 3000);
        };
        ws.onerror = () => {
          try {
            ws.close();
          } catch (_) {
            /* noop */
          }
        };
      } catch (e) {
        console.error('Quote WebSocket', e);
        reconnectTimer = setTimeout(connectWs, 5000);
      }
    };

    connectWs();

    // HTTP fallback：
    //   - WS 在线时跳过（已经在秒级推送了，HTTP 重复拉浪费）
    //   - 标签页不可见时跳过（用户没看，没必要烧 CPU）
    const isWsAlive = () => quoteWsRef.current && quoteWsRef.current.readyState === WebSocket.OPEN;
    const httpFallback = setInterval(() => {
      if (stopped) return;
      if (document.hidden) return;
      if (isWsAlive()) return;
      fetchRealTimePrices();
    }, QUOTE_HTTP_MS);

    return () => {
      stopped = true;
      clearInterval(httpFallback);
      if (reconnectTimer) clearTimeout(reconnectTimer);
      if (quoteWsRef.current) {
        quoteWsRef.current.close();
        quoteWsRef.current = null;
      }
    };
  }, [activeSymbols.join('|')]);

  // Get real-time price（库里有 currentPrice 可暂代；澎博暂无则用入场价参考，避免整卡 $0）
  const getCurrentPrice = (trade) => {
    if (trade.status !== 'active')
      return parseFloat(trade.currentPrice) || parseFloat(trade.exitPrice) || 0;

    const key = normSymbol(trade.symbol);
    let live = null;
    if (key && realTimePrices[key]) {
      const priceData = realTimePrices[key];
      if (!priceData.error) {
        const e = effectiveQuotePrice(priceData);
        if (!Number.isNaN(e)) live = e;
      }
    }
    if (live != null && live > 0) return live;
    const db = parseFloat(trade.currentPrice);
    if (!Number.isNaN(db) && db > 0) return db;
    if (live != null) return live;
    const entry = parseFloat(trade.entryPrice);
    if (!Number.isNaN(entry) && entry > 0) return entry;
    return parseFloat(trade.exitPrice) || 0;
  };

  /** 是否已有澎博/缓存有效报价（与卡片展示兜底区分�?*/
  const hasLiveQuoteForTrade = (trade) => {
    if (trade.status !== 'active') return false;
    const key = normSymbol(trade.symbol);
    const pdata = realTimePrices[key];
    return !!(pdata && !pdata.error && quotePayloadValid(pdata));
  };

  /** 当前展示价的来源�?psbangu' | 'sina' | null（库兜底�?*/
  const getQuoteSource = (trade) => {
    if (trade.status !== 'active') return null;
    const key = normSymbol(trade.symbol);
    const pdata = realTimePrices[key];
    if (!pdata || pdata.error || !quotePayloadValid(pdata)) return null;
    return pdata._quoteSource || 'psbangu';
  };

  /** 最近一次实际价格跳动的时间与方向（用于闪烁动画�?Last Updated�?*/
  const getTickMeta = (trade) => {
    if (trade.status !== 'active') return null;
    const key = normSymbol(trade.symbol);
    const pdata = realTimePrices[key];
    if (!pdata || pdata.error || pdata._quoteSource === 'storage') return null;
    if (!pdata._tickTs) return null;
    return { ts: pdata._tickTs, dir: pdata._tickDir || null };
  };

  /** HH:MM:SS（本地时间），跳动时间戳显示�?*/
  const formatTickTime = (ts) => {
    if (!ts) return '';
    try {
      const d = new Date(ts);
      const pad = (n) => String(n).padStart(2, '0');
      return `${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
    } catch (_) {
      return '';
    }
  };

  // Get price change information
  const getPriceChange = (symbol) => {
    const key = normSymbol(symbol);
    const row = key && realTimePrices[key];
    if (!row || row.error) return null;
    const pct = effectiveChangePercent(row);
    if (pct == null || Number.isNaN(pct)) return null;
    const pre = row.preClose != null && row.preClose !== '' ? parseFloat(row.preClose) : NaN;
    const px = effectiveQuotePrice(row);
    let ch = row.change != null && row.change !== '' ? parseFloat(row.change) : NaN;
    if (Number.isNaN(ch) && !Number.isNaN(pre) && !Number.isNaN(px)) ch = px - pre;
    return {
      change: Number.isNaN(ch) ? 0 : ch,
      changePercent: pct,
    };
  };

  const filters = [
    { id: 'all', label: 'All Trades' },
    { id: 'active', label: 'Active' },
    { id: 'closed', label: 'Closed' },
  ];

  const sortTradesByStatus = (list) =>
    [...list].sort((a, b) => {
      const rank = (status) => (status === 'active' ? 0 : 1);
      const byStatus = rank(a.status) - rank(b.status);
      if (byStatus !== 0) return byStatus;
      const ta = new Date(a.createdAt || a.entryDate || 0).getTime();
      const tb = new Date(b.createdAt || b.entryDate || 0).getTime();
      return tb - ta;
    });

  const filteredTrades = useMemo(() => {
    const filtered = trades.filter((trade) => {
      if (activeFilter === 'all') return true;
      return trade.status === activeFilter;
    });
    return sortTradesByStatus(filtered);
  }, [trades, activeFilter]);

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const getTradeImage = (symbol) => {
    const images = {
      'NVDA': 'https://images.unsplash.com/photo-1640340434855-6084b1f4901c?w=100&h=100&fit=crop',
      'AAPL': 'https://images.unsplash.com/photo-1611186871348-b1ce696e52c9?w=100&h=100&fit=crop',
      'TSLA': 'https://images.unsplash.com/photo-1617704548623-340376564e68?w=100&h=100&fit=crop',
      'AMZN': 'https://images.unsplash.com/photo-1523474253046-8cd2748b5fd2?w=100&h=100&fit=crop',
      'MSFT': 'https://images.unsplash.com/photo-1633419461186-7d40a38105ec?w=100&h=100&fit=crop',
    };
    return images[symbol] || 'https://images.unsplash.com/photo-1611974789855-9c2a0a7236a3?w=100&h=100&fit=crop';
  };

  // Get chart image URL (prefer real image, otherwise use mock image)
  const getChartImageUrl = (trade) => {
    // If trade record has real image URL, use it
    if (trade.image) {
      const fullUrl = getFullImageUrl(trade.image, null);
      if (fullUrl) return fullUrl;
    }
    
    // If no real image, return mock chart image
    return getMockChartImage(trade.symbol);
  };

  // 无外链依赖：不访问 via.placeholder.com（部分地区网络会 ERR_CONNECTION_CLOSED）
  const genericChartPlaceholder = (symbol) => {
    const s = String(symbol || '')
      .replace(/[<>&'"]/g, '')
      .slice(0, 14);
    const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="400" height="200" viewBox="0 0 400 200"><rect fill="#12151c" width="400" height="200"/><path d="M24 148 L72 118 L120 132 L168 88 L216 104 L264 64 L312 82 L360 56" stroke="#2563eb" stroke-width="2" fill="none" opacity="0.85"/><text x="200" y="182" fill="#8b95a8" font-family="system-ui,sans-serif" font-size="13" text-anchor="middle">${s} · chart</text></svg>`;
    return `data:image/svg+xml,${encodeURIComponent(svg)}`;
  };

  // Get mock chart image (generate different chart placeholders based on stock symbol)
  const getMockChartImage = (symbol) => {
    const chartImages = {
      'NVDA': 'https://images.unsplash.com/photo-1611974789855-9c2a0a7236a3?w=400&h=200&fit=crop',
      'TSLA': 'https://images.unsplash.com/photo-1590283603385-17ffb3a7f29f?w=400&h=200&fit=crop',
      'AAPL': 'https://images.unsplash.com/photo-1611186871348-b1ce696e52c9?w=400&h=200&fit=crop',
      'AMD': 'https://images.unsplash.com/photo-1640340434855-6084b1f4901c?w=400&h=200&fit=crop',
      'MSFT': 'https://images.unsplash.com/photo-1633419461186-7d40a38105ec?w=400&h=200&fit=crop',
      'AMZN': 'https://images.unsplash.com/photo-1523474253046-8cd2748b5fd2?w=400&h=200&fit=crop',
      'GOOGL': 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=400&h=200&fit=crop',
      'META': 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=400&h=200&fit=crop',
    };

    if (chartImages[symbol]) {
      return chartImages[symbol];
    }

    return genericChartPlaceholder(symbol);
  };

  const getStatusText = (trade) => {
    if (trade.status === 'active') return 'Active';
    return trade.statusLabel || 'Closed';
  };

  if (loading) {
    return (
      <div className="trade-records trade-records--loading">
        <div className="trade-records__spinner" aria-hidden="true"></div>
        <p className="trade-records__loading-text">Loading trade records…</p>
      </div>
    );
  }

  if (loadError && trades.length === 0) {
    return (
      <div className="trade-records trade-records--error">
        <div className="trade-records__error-icon" aria-hidden="true">⚠️</div>
        <p className="trade-records__error-title">Failed to load trade records</p>
        <p className="trade-records__error-desc">
          Slow network or the service is temporarily unavailable.
        </p>
        <button
          type="button"
          className="trade-records__retry-btn"
          onClick={() => fetchTrades(true)}
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="trade-records">
      {/* Header */}
      <div className="trade-records__header">
        <div className="trade-records__title-wrapper">
          <div className="trade-records__icon">
            <svg viewBox="0 0 24 24" fill="none" width="24" height="24">
              <path d="M9 5H7C5.89543 5 5 5.89543 5 7V19C5 20.1046 5.89543 21 7 21H17C18.1046 21 19 20.1046 19 19V7C19 5.89543 18.1046 5 17 5H15M9 5C9 6.10457 9.89543 7 11 7H13C14.1046 7 15 6.10457 15 5M9 5C9 3.89543 9.89543 3 11 3H13C14.1046 3 15 3.89543 15 5M12 12H15M12 16H15M9 12H9.01M9 16H9.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </div>
          <div>
            <h2 className="trade-records__title">Trading Records</h2>
            <p className="trade-records__subtitle">2026</p>
          </div>
        </div>

        {/* Filters */}
        <div className="trade-records__filters">
          {filters.map(filter => (
            <button
              key={filter.id}
              className={`trade-records__filter ${activeFilter === filter.id ? 'trade-records__filter--active' : ''}`}
              onClick={() => setActiveFilter(filter.id)}
            >
              {filter.label}
            </button>
          ))}
        </div>
      </div>

      {/* Trade Cards Grid */}
      <div className="trade-records__grid">
        {filteredTrades.length === 0 ? (
          <div className="trade-records__empty">
            <p>No trades found</p>
          </div>
        ) : (
          filteredTrades.map((trade, index) => {
            const entryPrice = parseFloat(trade.entryPrice) || 0;
            const currentPrice = getCurrentPrice(trade);
            const exitPrice = trade.status === 'active' ? currentPrice : (parseFloat(trade.exitPrice) || 0);
            const showingLive = trade.status === 'active' && hasLiveQuoteForTrade(trade);

            let pnlRatio, pnlAmount;
            if (trade.status === 'active' && currentPrice > 0 && entryPrice > 0) {
              pnlAmount = (currentPrice - entryPrice) * (trade.quantity || 0);
              pnlRatio = ((currentPrice - entryPrice) / entryPrice) * 100;
            } else {
              pnlRatio = parseFloat(trade.pnlRatio) || 0;
              pnlAmount = parseFloat(trade.pnlAmount) || 0;
            }

            const priceChange = trade.status === 'active' ? getPriceChange(trade.symbol) : null;
            const positionChangePercent =
              trade.status === 'active' && entryPrice > 0 && currentPrice > 0
                ? ((currentPrice - entryPrice) / entryPrice) * 100
                : null;
            const displayChange =
              priceChange ??
              (positionChangePercent != null
                ? { changePercent: positionChangePercent, change: currentPrice - entryPrice }
                : null);

            return (
              <div
                key={trade.id}
                className="trade-card"
                style={{ animationDelay: `${index * 0.05}s` }}
              >
                <div className="trade-card__header">
                  <div className="trade-card__header-top">
                    <span className="trade-card__market-badge">{trade.market || 'NASDAQ'}</span>
                    <span className="trade-card__symbol">{trade.symbol}</span>
                    <span className={`trade-card__status trade-card__status--${trade.status}`}>
                      {getStatusText(trade)}
                    </span>
                  </div>
                </div>

                <div className="trade-card__body">
                  <div className="trade-card__chart-area">
                    <img
                      src={getChartImageUrl(trade)}
                      alt={`${trade.symbol} Chart`}
                      className="trade-card__chart-image"
                      onClick={() => {
                        const url = getChartImageUrl(trade);
                        if (!url) return;
                        setPreviewError(false);
                        setPreviewLoading(true);
                        setPreviewImage(url);
                      }}
                      onError={(e) => {
                        e.target.style.display = 'none';
                        if (e.target.parentElement && !e.target.parentElement.querySelector('.trade-card__chart-placeholder')) {
                          const placeholder = document.createElement('div');
                          placeholder.className = 'trade-card__chart-placeholder';
                          placeholder.innerHTML = '<div class="trade-card__chart-placeholder-icon">📈</div><div class="trade-card__chart-placeholder-text">Chart Area</div>';
                          e.target.parentElement.appendChild(placeholder);
                        }
                      }}
                    />
                  </div>

                  <div className="trade-card__details">
                    <div className="trade-card__detail-row">
                      <div className="trade-card__detail-item">
                        <span className="trade-card__detail-label">Entry Date</span>
                        <span className="trade-card__detail-value">{formatDate(trade.entryDate)}</span>
                      </div>
                      <div className="trade-card__detail-item">
                        <span className="trade-card__detail-label">Entry Price</span>
                        <span className="trade-card__detail-value trade-card__detail-value--highlight">${entryPrice.toFixed(2)}</span>
                      </div>
                    </div>
                    {trade.exitDate && (
                      <div className="trade-card__detail-row">
                        <div className="trade-card__detail-item">
                          <span className="trade-card__detail-label">Exit Date</span>
                          <span className="trade-card__detail-value">{formatDate(trade.exitDate)}</span>
                        </div>
                        <div className="trade-card__detail-item">
                          <span className="trade-card__detail-label">Exit Price</span>
                          <span className="trade-card__detail-value trade-card__detail-value--highlight">${exitPrice.toFixed(2)}</span>
                        </div>
                      </div>
                    )}
                    {trade.status === 'active' && (() => {
                      const tickMeta = getTickMeta(trade);
                      const flashClass = tickMeta && tickMeta.dir
                        ? (tickMeta.dir === 'up'
                            ? 'trade-card__detail-value--flash-up'
                            : 'trade-card__detail-value--flash-down')
                        : '';
                      let upDownClass = 'trade-card__detail-value--up';
                      if (displayChange) {
                        upDownClass = displayChange.changePercent >= 0
                          ? 'trade-card__detail-value--up'
                          : 'trade-card__detail-value--down';
                      } else if (currentPrice > 0 && entryPrice > 0) {
                        upDownClass = currentPrice >= entryPrice
                          ? 'trade-card__detail-value--up'
                          : 'trade-card__detail-value--down';
                      }
                      return (
                        <div className="trade-card__detail-row">
                          <div className="trade-card__detail-item">
                            <span className="trade-card__detail-label">
                              Real-time Price
                              {!showingLive && (
                                <span className="trade-card__price-stale-hint" title="暂无接入的实时行情，当前为入场价或后台当前价参考">
                                  {' '}(参考)
                                </span>
                              )}
                            </span>
                            <div className="trade-card__realtime-price">
                              {showingLive ? (
                                <span className="trade-card__live-dot" aria-label="live" />
                              ) : null}
                              <span
                                key={tickMeta ? tickMeta.ts : 'static'}
                                className={`trade-card__detail-value trade-card__detail-value--realtime ${!showingLive ? 'trade-card__detail-value--reference' : ''} ${upDownClass} ${flashClass}`}
                              >
                                ${currentPrice.toFixed(2)}
                              </span>
                              {displayChange && (
                                <span className={`trade-card__price-change ${displayChange.changePercent >= 0 ? 'trade-card__price-change--up' : 'trade-card__price-change--down'}`}>
                                  {displayChange.changePercent >= 0 ? '▲' : '▼'} {Math.abs(displayChange.changePercent).toFixed(2)}%
                                </span>
                              )}
                            </div>
                          </div>
                          <div className="trade-card__detail-item">
                            <span className="trade-card__detail-label">Quantity</span>
                            <span className="trade-card__detail-value">{trade.quantity?.toLocaleString() || 0}</span>
                          </div>
                        </div>
                      );
                    })()}
                  </div>
                </div>

                <div className="trade-card__stats-grid">
                  <div className="trade-card__stat-box">
                    <span className="trade-card__stat-label">Input Amount</span>
                    <span className="trade-card__stat-value trade-card__stat-value--primary">
                      ${(entryPrice * (trade.quantity || 0)).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </span>
                  </div>
                  <div className="trade-card__stat-box">
                    <span className="trade-card__stat-label">Market Value</span>
                    <span className={`trade-card__stat-value ${pnlRatio >= 0 ? 'trade-card__stat-value--profit' : 'trade-card__stat-value--loss'}`}>
                      ${(exitPrice * (trade.quantity || 0)).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </span>
                  </div>
                  <div className="trade-card__stat-box">
                    <span className="trade-card__stat-label">P&L Ratio</span>
                    <span className={`trade-card__stat-value ${pnlRatio >= 0 ? 'trade-card__stat-value--profit' : 'trade-card__stat-value--loss'}`}>
                      {pnlRatio >= 0 ? '+' : ''}{pnlRatio.toFixed(2)}%
                    </span>
                  </div>
                  <div className="trade-card__stat-box">
                    <span className="trade-card__stat-label">P&L Amount</span>
                    <span className={`trade-card__stat-value ${pnlAmount >= 0 ? 'trade-card__stat-value--profit' : 'trade-card__stat-value--loss'}`}>
                      {pnlAmount >= 0 ? '+' : '-'}${Math.abs(pnlAmount).toLocaleString('en-US', { minimumFractionDigits: 2 })}
                    </span>
                  </div>
                </div>

                <div className={`trade-card__gradient ${pnlRatio >= 0 ? 'trade-card__gradient--profit' : 'trade-card__gradient--loss'}`}></div>
              </div>
            );
          })
        )}
      </div>

      {/* Image Preview Modal — portal 到 body，避免父级 transform 导致 fixed 黑屏 */}
      {previewImage && createPortal(
        (
          <div
            className="trade-records__preview-modal"
            onClick={closePreview}
            role="dialog"
            aria-modal="true"
            aria-label="Trade image preview"
          >
            <div className="trade-records__preview-content" onClick={(e) => e.stopPropagation()}>
              <button
                type="button"
                className="trade-records__preview-close"
                onClick={closePreview}
                aria-label="Close preview"
              >
                ×
              </button>
              {previewLoading && !previewError && (
                <div className="trade-records__preview-status">Loading image…</div>
              )}
              {previewError ? (
                <div className="trade-records__preview-status trade-records__preview-status--error">
                  Image failed to load. Please check the upload or try again later.
                </div>
              ) : (
                <img
                  src={previewImage}
                  alt="Trade Receipt Preview"
                  className="trade-records__preview-image"
                  onLoad={() => setPreviewLoading(false)}
                  onError={() => {
                    setPreviewLoading(false);
                    setPreviewError(true);
                  }}
                />
              )}
            </div>
          </div>
        ),
        document.body
      )}
    </div>
  );
};

export default TradeRecords;
